import { Link } from "react-router-dom"
import { Chart, TableVisualization } from "~/ui-rtk/components/ui/charts"
import type { TChartProps } from "~/ui-rtk/components/ui/charts/Chart"
import { Loader } from "~/ui-rtk/components/ui/common"
import { AggregationType, VISUALIZATION_TYPE } from "~/ui-rtk/constants/charts"
import { CustomAttributionBoard } from "../components/CustomAttributionBoard"
import { RevenueBreakdownContainer } from "./RevenueBreakdownContainer"
import { VISUALIZATION_TABLE, TBrandMediaDashboardCharts } from "../constants"
import type { RowSelectionState } from "@tanstack/react-table"
import type { TableVisualizationColumn } from "~/ui-rtk/constants/table-visualization-types"
import type { TCubeFilterOptions } from "~/ui-rtk/shared/types/charts"
import type { TBrandMediaOptimizationTable } from "~/ui-rtk/pages/BrandMedia/PaidMediaWeights/connect"

interface ContentContainerProps {
  activeWidget?: TBrandMediaOptimizationTable
  isLoading: boolean
  highlight: boolean
  dateRange: [Date?, Date?]
  items: TBrandMediaDashboardCharts
  cubeFilters: TCubeFilterOptions
  rowSelection: RowSelectionState
  onRowSelectionChange: (selection: RowSelectionState) => void
  sources: Map<string, { name: string; icon: string }>
  queryPrefix?: string
  chartPropsSorting: Array<{ id: string; desc: boolean }>
  defaultColumnKeys?: string[]
  columnConfigFiltered: TableVisualizationColumn[]
  chartProps?: Record<string, unknown>
  brandMediaId: string
  sourceKeys?: string[]
  adsetData?: Record<string, string | undefined>[]
  isAdsetDataLoading?: boolean
}

export function ContentContainer({
  activeWidget,
  isLoading,
  highlight,
  dateRange,
  items,
  cubeFilters,
  rowSelection,
  onRowSelectionChange,
  sources,
  queryPrefix,
  chartPropsSorting,
  defaultColumnKeys,
  columnConfigFiltered,
  chartProps,
  brandMediaId,
  adsetData,
  isAdsetDataLoading,
}: ContentContainerProps) {
  const renderTable = () => {
    const chartPropsWithOverrides = {
      ...chartProps,
      highlightCells: Boolean(chartProps?.highlightCells) && highlight,
      chartId: activeWidget?.widget ?? "",
      urlParamsPrefix: brandMediaId,
      className: "",
      widgetType: VISUALIZATION_TYPE.TableVisualization,
      component: TableVisualization,
      dateRange: dateRange as [Date, Date],
      granularity: AggregationType.DAILY,
      metrics: items,
      cubeFilters,
      enableRowSelection: true,
      rowSelection,
      onRowSelectionChange,
      sources,
      requiredSources: activeWidget?.requiredSources,
      hideAggSelector: true,
      queryPrefix,
      useOrderQueryPrefix: true,
      defaultSorting: chartPropsSorting,
      defaultColumnKeys,
      useLocalStoragePagination: !activeWidget?.summaryWidget,
      columnConfig: columnConfigFiltered,
    } as TChartProps

    return (
      <div className="mt-3">
        <Chart {...chartPropsWithOverrides} />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="h-100 w-full flex items-center justify-center">
        <Loader size={50} />
      </div>
    )
  }

  const renderWidgetContent = () => {
    if (!activeWidget) {
      return (
        <div>
          It seems like you don't have active connection to show anything on
          this page. Please{" "}
          <Link className="link" to="/setup/connectors" title="Connector page">
            review your connections.
          </Link>
        </div>
      )
    }

    if (activeWidget.id === VISUALIZATION_TABLE.CUSTOM_ATTRIBUTION) {
      return (
        <CustomAttributionBoard
          adsetData={adsetData}
          isAdsetDataLoading={isAdsetDataLoading}
        />
      )
    }

    if (activeWidget.id === VISUALIZATION_TABLE.REVENUE_BREAKDOWN) {
      return (
        <RevenueBreakdownContainer
          dateRange={dateRange}
          adsetData={adsetData}
          isAdsetDataLoading={isAdsetDataLoading}
        />
      )
    }

    return renderTable()
  }

  return <div className="mt-3">{renderWidgetContent()}</div>
}

export default ContentContainer
